/**
 * Created by neo on 22.11.2023
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { Audited, createAuditedFields } from '../../Audited';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { RouteChallengeTeamMember, createRouteChallengeTeamMember } from './RouteChallengeTeamMember';
import { Media, createMedia, getMediaMedium } from '../../Media/Media';
import {
  RouteChallengeTeamReachedLocation,
  createRouteChallengeTeamReachedLocation,
} from './RouteChallengeTeamReachedLocation';
import { RouteChallengeTeamJoinRequest, createRouteChallengeTeamJoinRequest } from './RouteChallengeTeamJoinRequest';
import { Pageable } from '../../Interfaces/Pageable';
import { RouteChallengeLocationPosition, createRouteChallengeLocationPosition } from './RouteChallengeLocationPosition';
import { RouteChallengeLocation, createRouteChallengeLocation } from './RouteChallengeLocation';
import { logger } from '../../../Utils/logger';

export type RouteChallengeTeamRequest = Pageable & {
  query?: string;
  gymId?: string;
  challengeId?: string;
  latitude?: number;
  longitude?: number;
  maxDistance?: number;
};

export type RouteChallengeRankingRequest = Pageable & {};

export type RouteChallengeTeam = Audited & {
  challengeId: string;
  name: string;
  image?: Media;
  isPrivate: boolean;
  totalCollectedPoints: number;
  reachedLocations: RouteChallengeTeamReachedLocation[];
  currentLocation?: RouteChallengeLocation;
  nextLocation?: RouteChallengeLocation;
  _reachedLocationsCount?: number;
  memberCount: number;
  maxAllowedMembers: number;
  minAllowedMembers: number;
  ownerCustomerId?: string;
  currentPosition: RouteChallengeLocationPosition;
  globalRank: number;
  companyRank: number;
  completedDateTime?: Date;
  members?: RouteChallengeTeamMember[];
  readonly imageSource: any | undefined;
  readonly hasSeatsAvailable: boolean;
  readonly membersSortedByPoints: RouteChallengeTeamMember[];
  readonly coordinates: { latitude: number; longitude: number };
  readonly reachedLocationsCount: number;
  save(): Promise<RouteChallengeTeam>;
  delete(): Promise<RouteChallengeTeam>;
  join(inviteCode?: string): Promise<RouteChallengeTeam>;
  leave(): Promise<RouteChallengeTeam>;
  removeMember(memberId: string): Promise<RouteChallengeTeam>;
  requests(): Promise<RouteChallengeTeamJoinRequest[]>;
  updatePosition(): Promise<RouteChallengeTeam>;
  deleteTeeamPicture(): void;
};

export function createRouteChallengeTeam(json?: Partial<RouteChallengeTeam>): RouteChallengeTeam {
  const audited = createAuditedFields(json);
  const team: RouteChallengeTeam = makeAutoObservable({
    ...audited,
    challengeId: json?.challengeId ?? '',
    name: json?.name ?? '',
    image: json?.image ? createMedia(json.image as any) : undefined,
    isPrivate: json?.isPrivate ?? false,
    totalCollectedPoints: json?.totalCollectedPoints ?? 0,
    reachedLocations: ((json?.reachedLocations as any) ?? []).map((l: any) => createRouteChallengeTeamReachedLocation(l)),
    _reachedLocationsCount: (json as any)?.reachedLocationsCount,
    currentLocation: json?.currentLocation ? createRouteChallengeLocation(json.currentLocation as any) : undefined,
    nextLocation: json?.nextLocation ? createRouteChallengeLocation(json.nextLocation as any) : undefined,
    memberCount: json?.memberCount ?? 0,
    maxAllowedMembers: json?.maxAllowedMembers ?? 0,
    minAllowedMembers: json?.minAllowedMembers ?? 0,
    ownerCustomerId: json?.ownerCustomerId,
    currentPosition: createRouteChallengeLocationPosition(json?.currentPosition as any),
    globalRank: json?.globalRank ?? 0,
    companyRank: json?.companyRank ?? 0,
    completedDateTime: json?.completedDateTime ? new Date(json.completedDateTime as any) : undefined,
    members: undefined as RouteChallengeTeamMember[] | undefined,

    get imageSource(): any | undefined {
      if (this.image) {
        return { uri: getMediaMedium(this.image) };
      }
      return undefined;
    },

    get hasSeatsAvailable(): boolean {
      return this.memberCount < this.maxAllowedMembers;
    },

    get membersSortedByPoints(): RouteChallengeTeamMember[] {
      return this.members?.slice().sort((a, b) => b.totalCollectedPoints - a.totalCollectedPoints) ?? [];
    },

    get coordinates(): { latitude: number; longitude: number } {
      return { longitude: this.currentPosition.yorLng, latitude: this.currentPosition.xorLat };
    },

    get reachedLocationsCount(): number {
      return this._reachedLocationsCount ?? Math.max(0, this.reachedLocations.length - 1);
    },

    save(): Promise<RouteChallengeTeam> {
      return HttpBackend.post('/coach-program/engagement/route-challenges/teams', {
        teamId: this.id,
        challengeId: this.challengeId,
        name: this.name,
        isPrivate: this.isPrivate,
        image: this.image,
      }).then((response) => createRouteChallengeTeam(response));
    },

    delete(): Promise<RouteChallengeTeam> {
      return HttpBackend.delete(`/coach-program/engagement/route-challenges/teams/${this.id}`).then(() => this);
    },

    join(inviteCode?: string): Promise<RouteChallengeTeam> {
      return HttpBackend.post(`/coach-program/engagement/route-challenges/teams/${this.id}/join`, { inviteCode }).then(() => this);
    },

    leave(): Promise<RouteChallengeTeam> {
      return HttpBackend.post(`/coach-program/engagement/route-challenges/teams/${this.id}/leave`).then(() => this);
    },

    removeMember(memberId: string): Promise<RouteChallengeTeam> {
      return HttpBackend.delete(`/coach-program/engagement/route-challenges/teams/${this.id}/members/${memberId}`).then(() => this);
    },

    requests(): Promise<RouteChallengeTeamJoinRequest[]> {
      return HttpBackend.get(`/coach-program/engagement/route-challenges/teams/${this.id}/join-requests`).then((response) =>
        response.map((r: any) => createRouteChallengeTeamJoinRequest(r)),
      );
    },

    updatePosition(): Promise<RouteChallengeTeam> {
      return HttpBackend.get(`/coach-program/engagement/route-challenges/teams/${this.id}/position`)
        .then((res) => runInAction(() => (this.currentPosition = createRouteChallengeLocationPosition(res))))
        .then(() => this);
    },

    deleteTeeamPicture() {
      this.image = undefined;
    },
  });

  return team;
}

export const RouteChallengeTeamApi = {
  find(request?: Partial<RouteChallengeTeamRequest>): Promise<RouteChallengeTeam[]> {
    logger('RouteChallengeTeam.find', request);
    return HttpBackend.get('/coach-program/engagement/route-challenges/teams', request).then((response) =>
      response.map((r: any) => createRouteChallengeTeam(r)),
    );
  },

  findOne(teamId: string): Promise<RouteChallengeTeam | undefined> {
    return HttpBackend.get(`/coach-program/engagement/route-challenges/teams/${teamId}`).then((response) =>
      response ? createRouteChallengeTeam(response) : undefined,
    );
  },

  count(request?: Partial<RouteChallengeTeamRequest>): Promise<number> {
    return HttpBackend.get('/coach-program/engagement/route-challenges/teams/count', request).then((res) => res ?? 0);
  },

  myTeam(challengeId: string): Promise<RouteChallengeTeam> {
    return HttpBackend.get(`/coach-program/engagement/route-challenges/teams/my-team`, { challengeId }).then(
      (response) => response && createRouteChallengeTeam(response),
    );
  },

  ranking(challengeId: string, request?: Partial<RouteChallengeRankingRequest>): Promise<RouteChallengeTeam[]> {
    return HttpBackend.get(`/coach-program/engagement/route-challenges/teams`, {
      ...request,
      challengeId,
      sort: 'companyRank,ASC',
    }).then((response) => response.map((r: any) => createRouteChallengeTeam(r)));
  },

  fetchMembers(teamId: string): Promise<RouteChallengeTeamMember[]> {
    return HttpBackend.get(`/coach-program/engagement/route-challenges/teams/${teamId}/members`).then((response) =>
      response.map((r: any) => createRouteChallengeTeamMember(r)),
    );
  },
};
